// Bilder in der Vault-Vorschau: Obsidian-Syntax auf die Vault-API umschreiben
// und die Breite aus `|500` bzw. `|500x300` uebernehmen.
//
// Frueher wurde ein Bild mit Breite VOR `marked` zu rohem `<img …>` HTML.
// Steht so ein Tag allein in der Zeile, beginnt nach CommonMark ein HTML-Block,
// der alles bis zur naechsten Leerzeile roh durchreicht — die Bildunterschrift
// direkt darunter (`> ==MRT axial==: **…** →`) stand dadurch mit `>` und `**`
// im Klartext da. Jetzt bleibt das Bild Markdown (`![alt|500](url)`), und erst
// der Image-Renderer unten setzt die Breite.
//
// Ohne DOM, damit `node --test` es laden kann.
import MarkdownIt from 'markdown-it';

const BILD = 'png|jpg|jpeg|gif|svg|webp';
const GROESSE = /\|\s*(\d+)(?:x(\d+))?\s*$/;

// encodeURI laesst `(` und `)` stehen — die beenden aber das Markdown-Ziel.
const zielUrl = (pfad) =>
    '/api/obsidian/file/' + encodeURI(pfad).replace(/\(/g, '%28').replace(/\)/g, '%29');

/** `![[bild.png|500]]` und `![alt|500](Bilder/x.png)` → `![alt|500](/api/obsidian/file/…)`. */
export function bilderUmschreiben(inhalt) {
    // Obsidian duldet Leerzeichen um die Pipe und vor `]]`: `![[x.png | 500]]`.
    const wikilink = new RegExp(`!\\[\\[\\s*([^\\]|]+?\\.(?:${BILD}))\\s*(?:\\|\\s*([^\\]]+?))?\\s*\\]\\]`, 'gi');
    inhalt = inhalt.replace(wikilink, (_, pfad, groesse) => {
        pfad = pfad.trim();
        // Ohne Ordner liegt das Bild in `Bilder/`.
        const voll = /[/\\]/.test(pfad) ? pfad : 'Bilder/' + pfad;
        const zusatz = groesse && /^\d+(?:x\d+)?$/.test(groesse.trim()) ? `|${groesse.trim()}` : '';
        return `![${zusatz}](${zielUrl(voll)})`;
    });

    // Relative Pfade loeste der Browser sonst gegen die Seiten-URL auf und
    // landete im SPA-Fallback (es gibt keine Route /Bilder).
    const markdown = new RegExp(`!\\[([^\\]]*)\\]\\(([^)\\s]+\\.(?:${BILD}))\\)`, 'gi');
    inhalt = inhalt.replace(markdown, (treffer, alt, url) => {
        if (/^(https?:)?\/\//i.test(url) || url.startsWith('/api/') || url.startsWith('data:')) {
            return treffer;
        }
        const sauber = url.replace(/^\.\//, '').replace(/^\/+/, '');
        // Schon kodierte Pfade (`%20`) nicht doppelt kodieren.
        let pfad = sauber;
        try {
            pfad = decodeURI(sauber);
        } catch {
            // kaputtes `%`: so lassen, wie es steht
        }
        return `![${alt}](${zielUrl(pfad)})`;
    });
    return inhalt;
}

const attr = (s) => String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Die Vorschau rendert mit markdown-it statt marked: marked 11 paarte
// Fettdruck neben `<`/`>` falsch — `**Korsett bei < 40°**, Fusion **außer bei
// > 50-60°**` wurde ein einziger fetter Bogen mit `**` mittendrin (56 Stellen
// in 17 Notizen am 21.10.2026). markdown-it haelt sich an CommonMark und
// rendert ohnehin schon den Chat (markdown-renderer.js).
const md = new MarkdownIt({
    html: true, // <mark> aus `markierungen` und Rohes HTML der Notizen
    breaks: true, // Obsidian bricht bei jedem Zeilenumbruch
    linkify: false,
    typographer: false,
});

md.renderer.rules.image = (tokens, idx, options, env, self) => {
    const token = tokens[idx];
    const alt = self.renderInlineAsText(token.children, options, env);
    const m = alt.match(GROESSE);
    let style = '';
    if (m) {
        const [, b, h] = m;
        style = h
            ? `width: ${b}px; max-width: 100%; height: auto; aspect-ratio: ${b} / ${h};`
            : `width: ${b}px; max-width: 100%; height: auto;`;
    }
    const rein = m ? alt.slice(0, m.index).trim() : alt;
    const titel = token.attrGet('title');
    let out = `<img src="${attr(token.attrGet('src'))}" alt="${attr(rein)}"`;
    if (titel) out += ` title="${attr(titel)}"`;
    if (style) out += ` style="${style}"`;
    return out + '>';
};

/** Markdown der Vault (nach `bilderUmschreiben`) → HTML. */
export function vaultHtml(text) {
    return md.render(text);
}
